import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { Home, History, Activity as ActivityIcon, User } from "lucide-react";
import { getToken } from "@/utils/auth";

function resolveApiUrl() {
  const envApiUrl = import.meta.env.VITE_API_URL as string | undefined;
  const fallback = `${window.location.protocol}//${window.location.hostname}:4000`;
  if (!envApiUrl) return fallback;
  try {
    const u = new URL(envApiUrl);
    if (u.hostname === "localhost" || u.hostname === "127.0.0.1") return fallback;
    return envApiUrl;
  } catch {
    return envApiUrl || fallback;
  }
}

// Activity previously reused the History icon, which looked identical to the Sales History tab —
// give each its own distinct icon so they're easy to tell apart at a glance.
const navItems = [
  { path: "/app", label: "Home", icon: Home },
  { path: "/app/sales/history", label: "Sales History", icon: History, showOutstandingBadge: true },
  { path: "/app/activity", label: "Activity", icon: ActivityIcon },
  { path: "/app/profile", label: "Profile", icon: User },
] as const;

export default function SupervisorLayout() {
  const location = useLocation();
  const apiUrl = useMemo(() => resolveApiUrl(), []);
  const [outstandingSalesCount, setOutstandingSalesCount] = useState(0);

  const loadOutstandingSalesCount = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setOutstandingSalesCount(0);
      return;
    }
    try {
      const res = await fetch(`${apiUrl}/supervisor/team-sales/outstanding-count`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json: { count?: number } = await res.json().catch(() => ({}));
      if (!res.ok) return;
      const count = Number(json.count ?? 0);
      setOutstandingSalesCount(Number.isFinite(count) && count > 0 ? Math.floor(count) : 0);
    } catch {
      // Keep the last known count on transient failures.
    }
  }, [apiUrl]);

  useEffect(() => {
    void loadOutstandingSalesCount();
  }, [loadOutstandingSalesCount, location.pathname]);

  useEffect(() => {
    const onOutstandingChanged = () => {
      void loadOutstandingSalesCount();
    };
    window.addEventListener("sales-outstanding-changed", onOutstandingChanged);
    return () => window.removeEventListener("sales-outstanding-changed", onOutstandingChanged);
  }, [loadOutstandingSalesCount]);

  const isActive = (path: string) => {
    if (path === "/app") {
      return (
        location.pathname === "/app" ||
        location.pathname.startsWith("/app/stock") ||
        (location.pathname.startsWith("/app/sales") && !location.pathname.startsWith("/app/sales/history"))
      );
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="flex flex-col h-screen max-w-md mx-auto bg-white shadow-xl relative">
      <main className="flex-1 overflow-y-auto bg-white pb-16">
        <Outlet />
      </main>

      <nav className="fixed bottom-0 left-0 right-0 mx-auto w-full max-w-md bg-white border-t border-gray-200 flex justify-around items-center h-[60px] z-50">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          const badgeCount =
            "showOutstandingBadge" in item && item.showOutstandingBadge ? outstandingSalesCount : 0;

          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
                active ? "text-primary" : "text-gray-500 hover:text-primary"
              }`}
            >
              <div className="relative mb-1">
                <Icon size={22} />
                {badgeCount > 0 ? (
                  <span className="absolute -top-1.5 -right-2.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center leading-none ring-2 ring-white">
                    {badgeCount > 99 ? "99+" : badgeCount}
                  </span>
                ) : null}
              </div>
              <span className="text-[11px] leading-3 font-medium whitespace-nowrap">{item.label}</span>
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
